(function () {
  "use strict";

  var MAX_AGE = 60 * 60 * 24 * 90; // 90 days, in seconds

  // Persisted identifiers. Click ids first, then UTM (unchanged from before).
  //
  // 2026-08-24: `fbclid` added. Meta issues it on every ad click, exactly like
  // Google issues `gclid` — before this it was the only major platform whose
  // receipt we threw away. Nothing else in this list changed.
  var CLICK_KEYS = [
    "gclid", "gbraid", "wbraid", // Google (gbraid/wbraid are the iOS variants)
    "fbclid",                    // Meta
    "msclkid",                   // Microsoft / Bing Ads
    "ttclid",                    // TikTok
    "epik",                      // Pinterest
    "li_fat_id",                 // LinkedIn
    "twclid"                     // X / Twitter
  ];
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var TRACK_KEYS = CLICK_KEYS.concat(UTM_KEYS);

  // Extra context (written once, on the first visit).
  var LANDING_KEY = "landing_page";
  var FIRST_SEEN_KEY = "gclid_first_seen_at";

  // Meta browser cookies, set by the Meta pixel — NOT URL parameters, so they
  // are never captured by `capture()` below; they are read straight from the
  // cookie jar at send time. `_fbc` is what the Conversions API matches on.
  //
  // We deliberately do NOT write these back with setStore(): they belong to the
  // pixel, and re-writing them with our own max-age would fight its own cookie
  // management. Read-only, always current.
  var META_COOKIE_KEYS = ["_fbc", "_fbp"];

  // Other first-party cookies worth forwarding, for the same reason: they are
  // set by tags that load AFTER us, and they are the durable half of a click.
  //   _gcl_aw — Google Ads' own click cookie; outlives the gclid URL param.
  //   _ga     — GA4 client id; the join key if we ever reconcile with GA4.
  // Nothing reads these on our side yet. They ride into Shopify's
  // note_attributes, which Shopify keeps forever — so a column can be added
  // later and backfilled from the Shopify API without touching this theme again.
  var EXTRA_COOKIES = [
    { cookie: "_gcl_aw", field: "gcl_aw" },
    { cookie: "_ga", field: "ga_client_id" }
  ];

  // When `fbclid` is seen, remember WHEN — `_fbc` is built from that timestamp
  // if the pixel cookie is missing (blocked, cleared, or pixel not yet loaded).
  var FBCLID_SEEN_KEY = "fbclid_seen_at";

  // Fields forwarded into contact forms (+ page_url, computed per submit).
  // Meta cookies travel under names without the leading underscore, so the
  // hidden inputs stay valid `contact[...]` field names.
  // The referrer of the FIRST visit. The single most useful thing we were not
  // capturing: when a click carries no parameter at all, this is the only
  // remaining evidence of where the person came from.
  var REFERRER_KEY = "first_referrer";

  var FORM_FIELDS = TRACK_KEYS.concat([
    LANDING_KEY, FIRST_SEEN_KEY, REFERRER_KEY, "fbc", "fbp", "page_url"
  ]).concat(EXTRA_COOKIES.map(function (c) { return c.field; }));

  // ---------- storage helpers (localStorage primary, cookie fallback) ----------

  function setStore(key, value) {
    if (!value) return; // never overwrite an existing value with empty
    try { localStorage.setItem(key, value); } catch (e) {}
    try {
      document.cookie = key + "=" + encodeURIComponent(value) +
        "; path=/; max-age=" + MAX_AGE + "; SameSite=Lax";
    } catch (e) {}
  }

  function getStore(key) {
    try { var v = localStorage.getItem(key); if (v) return v; } catch (e) {}
    var m = document.cookie.match(
      "(?:^|; )" + key.replace(/([.*+?^${}()|[\]\\])/g, "\\$1") + "=([^;]*)"
    );
    return m ? decodeURIComponent(m[1]) : "";
  }

  // Cookie-only read — used for the pixel's own cookies, which must never be
  // shadowed by a stale localStorage copy.
  function getCookie(name) {
    var m = document.cookie.match(
      "(?:^|; )" + name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1") + "=([^;]*)"
    );
    return m ? decodeURIComponent(m[1]) : "";
  }

  // ---------- Meta: resolve _fbc / _fbp ----------

  // `_fbc` format is `fb.<subdomainIndex>.<creationTimeMs>.<fbclid>`.
  // subdomainIndex is 1 for a bare domain like inkohoreca.com.
  function resolveFbc() {
    var cookie = getCookie("_fbc");
    if (cookie) return cookie; // the pixel's own value always wins

    var fbclid = getStore("fbclid");
    if (!fbclid) return "";

    var seenAt = parseInt(getStore(FBCLID_SEEN_KEY), 10);
    if (!seenAt) return ""; // no timestamp — a fabricated one would not match

    return "fb.1." + seenAt + "." + fbclid;
  }

  function resolveMeta() {
    return { fbc: resolveFbc(), fbp: getCookie("_fbp") };
  }

  // Cookie-backed extras, resolved at send time (they may not exist at init).
  function resolveExtras() {
    var out = {};
    EXTRA_COOKIES.forEach(function (c) {
      var v = getCookie(c.cookie);
      if (v) out[c.field] = v;
    });
    return out;
  }

  // ---------- 1) capture from URL (last-touch wins; empty never overwrites) ----

  function capture() {
    var params = new URLSearchParams(window.location.search);
    TRACK_KEYS.forEach(function (key) {
      var val = params.get(key);
      if (val) setStore(key, val);
    });

    // Stamp the fbclid arrival time whenever a NEW fbclid arrives in the URL —
    // the timestamp must belong to this click, not to the first ever visit.
    var fbclid = params.get("fbclid");
    if (fbclid) setStore(FBCLID_SEEN_KEY, String(Date.now()));

    if (!getStore(LANDING_KEY)) {
      setStore(LANDING_KEY, window.location.href);
      setStore(FIRST_SEEN_KEY, new Date().toISOString());
      // First visit only — a later internal page view would overwrite the real
      // referrer with our own domain, which is worse than having none.
      if (document.referrer && document.referrer.indexOf(location.host) === -1) {
        setStore(REFERRER_KEY, document.referrer);
      }
    }
  }

  // ---------- 2) fill native Shopify contact forms ----------

  function ensureField(form, name, value) {
    var input = form.querySelector('input[name="contact[' + name + ']"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "contact[" + name + "]";
      form.appendChild(input);
    }
    input.value = value || "";
  }

  function fillContactForms() {
    var meta = resolveMeta();
    var extras = resolveExtras();
    document.querySelectorAll('form[action*="/contact"]').forEach(function (form) {
      FORM_FIELDS.forEach(function (name) {
        var value;
        if (name === "page_url") value = window.location.href;
        else if (name === "fbc") value = meta.fbc;
        else if (name === "fbp") value = meta.fbp;
        else if (extras[name] !== undefined) value = extras[name];
        else value = getStore(name);
        ensureField(form, name, value);
      });
    });
  }

  // ---------- 3) cart attributes for direct purchases ----------

  function collectAttrs() {
    var attrs = {};
    TRACK_KEYS.concat([LANDING_KEY, FIRST_SEEN_KEY, REFERRER_KEY]).forEach(function (key) {
      var v = getStore(key);
      if (v) attrs[key] = v;
    });

    // Meta cookies ride along under underscore-free names, so they survive
    // Shopify's note_attributes without looking like hidden attributes.
    var meta = resolveMeta();
    if (meta.fbc) attrs.fbc = meta.fbc;
    if (meta.fbp) attrs.fbp = meta.fbp;

    var extras = resolveExtras();
    Object.keys(extras).forEach(function (k) { attrs[k] = extras[k]; });

    return attrs;
  }

  function stampCart() {
    var attrs = collectAttrs();
    if (!Object.keys(attrs).length) return; // nothing to stamp

    // Skip repeat POSTs within the same session unless the data changed.
    var sig = JSON.stringify(attrs);
    try {
      if (sessionStorage.getItem("__inko_cart_sig") === sig) return;
      sessionStorage.setItem("__inko_cart_sig", sig);
    } catch (e) {}

    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attrs })
      });
    } catch (e) {}
  }

  // ---------- wiring ----------

  // Re-fill contact fields right before submit (covers late DOM changes).
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (f && f.action && f.action.indexOf("/contact") !== -1) fillContactForms();
  }, true);

  // Re-stamp cart on add-to-cart and on checkout intent.
  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (f && f.action && f.action.indexOf("/cart/add") !== -1) stampCart();
  }, true);
  document.addEventListener("click", function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[name="add"], [name="checkout"], a[href*="/checkout"], a[href="/cart"]')) {
      stampCart();
    }
  }, true);

  // Forms injected after load (modals, section re-renders) — debounced.
  if (window.MutationObserver) {
    var t = null;
    var mo = new MutationObserver(function () {
      if (t) return;
      t = setTimeout(function () { t = null; fillContactForms(); }, 250);
    });
    try { mo.observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
  }

  // The Meta pixel usually loads AFTER this script, so `_fbp` (and sometimes
  // `_fbc`) do not exist at init. Re-stamp once shortly after load, otherwise
  // the very first cart of a session would go out without them.
  setTimeout(function () { fillContactForms(); stampCart(); }, 3000);

  // ---------- init ----------

  function init() {
    capture();
    fillContactForms();
    stampCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
